import './MGRSNode.css';
import type {
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  Spread,
} from 'lexical';
import { $applyNodeReplacement, TextNode } from 'lexical';

export type SerializedMGRSNode = Spread<
  {
    lat: number;
    lon: number;
  },
  SerializedTextNode
>;

export class MGRSNode extends TextNode {
  lat: number;
  lon: number;

  static override getType(): string {
    return 'mgrs';
  }

  static override clone(node: MGRSNode): MGRSNode {
    return new MGRSNode(node.lat, node.lon, node.__text, node.__key);
  }

  constructor(lat: number, lon: number, text: string, key?: NodeKey) {
    super(text, key);
    this.lat = lat;
    this.lon = lon;
  }

  /**
   * Check if the current text is still a valid MGRS coordinate
   */
  isValidMGRS(): boolean {
    const coords = mgrsToLatLon(this.__text);
    return coords !== null;
  }

  override createDOM(config: EditorConfig): HTMLElement {
    const a = document.createElement('a');
    const isValid = this.isValidMGRS();
    a.className = isValid ? 'mgrs-link' : 'mgrs-link-invalid';
    a.href = `https://maps.google.com/?q=${this.lat},${this.lon}`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.textContent = this.__text;
    a.setAttribute('spellcheck', 'false');
    a.setAttribute('data-lat', String(this.lat));
    a.setAttribute('data-lon', String(this.lon));
    a.setAttribute('data-valid', String(isValid));
    a.ondblclick = event => {
      if (!this.isValidMGRS()) return;
      event.preventDefault();
      window.open(a.href, '_blank', 'noopener');
    };

    return a;
  }

  override updateDOM(
    prevNode: this,
    dom: HTMLElement,
    config: EditorConfig,
  ): boolean {
    if (dom.tagName !== 'A') {
      return true;
    }

    const isValid = this.isValidMGRS();
    dom.textContent = this.__text;
    dom.className = isValid ? 'mgrs-link' : 'mgrs-link-invalid';
    dom.setAttribute(
      'href',
      `https://maps.google.com/?q=${this.lat},${this.lon}`,
    );
    dom.setAttribute('data-lat', String(this.lat));
    dom.setAttribute('data-lon', String(this.lon));
    dom.setAttribute('data-valid', String(isValid));
    dom.addEventListener('dblclick', event => {
      if (!this.isValidMGRS()) return;
      event.preventDefault();
      window.open(dom.getAttribute('href') ?? '', '_blank', 'noopener');
    });

    return false;
  }

  static override importJSON(serializedNode: SerializedMGRSNode): MGRSNode {
    return $createMGRSNode(
      serializedNode.lat,
      serializedNode.lon,
      serializedNode.text,
    ).updateFromJSON(serializedNode);
  }

  override exportJSON(): SerializedMGRSNode {
    return {
      lat: this.lat,
      lon: this.lon,
      ...super.exportJSON(),
    };
  }

  override canInsertTextAfter(): boolean {
    return true;
  }
}

export function $isMGRSNode(
  node: LexicalNode | null | undefined,
): node is MGRSNode {
  return node instanceof MGRSNode;
}

export function $createMGRSNode(
  lat: number,
  lon: number,
  text: string,
): MGRSNode {
  const node = new MGRSNode(lat, lon, text).setMode('segmented');
  return $applyNodeReplacement(node);
}

function isMGRSString(str: string): boolean {
  // Checks for a basic MGRS format: ZONE BAND SQUARE EASTING NORTHING
  // Ensures EASTING and NORTHING are numeric and of the same length
  const mgrsRegex = /^\d{1,2}\s*[C-HJ-NP-X]\s*[A-Z]{2}\s+\d+\s+\d+$/;
  if (!mgrsRegex.test(str)) return false;

  const parts = str.trim().split(/\s+/);
  if (parts.length !== 4) return false;

  const easting = parts[2]!;
  const northing = parts[3]!;
  return (
    /^\d+$/.test(easting) &&
    /^\d+$/.test(northing) &&
    easting.length === northing.length
  );
}

// Source - https://stackoverflow.com/a/46728364
// Posted by Steve Brooker
// Retrieved 2026-02-22, License - CC BY-SA 3.0
export function mgrsToLatLon(a: string): [number, number] | null {
  a = a.trim().toUpperCase();
  if (!isMGRSString(a)) return null;
  var split = a.replace(/\s/g, '').match(/\d+|[a-z]+/gi);
  if (split == null || split.length != 3) return null;
  var gridZoneNum = +split[0];
  var gridZoneAlpha = split[1]!.charAt(0);
  var gridSquare = split[1]!.substring(1);
  var easting = +split[2]!.substring(0, split[2]!.length / 2).padEnd(5, '0');
  var northing = +split[2]!.substring(split[2]!.length / 2).padEnd(5, '0');

  if (gridZoneNum < 1 || gridZoneNum > 60) return null;
  if (
    gridZoneAlpha < 'C' ||
    gridZoneAlpha > 'X' ||
    gridZoneAlpha === 'I' ||
    gridZoneAlpha === 'O'
  )
    return null;
  if (gridSquare.length !== 2) return null;
  if (!/^[A-Z]{2}$/.test(gridSquare)) return null;
  if (split[2]!.length % 2 !== 0) return null;

  var e = ((+gridZoneNum * 6 - 183) * Math.PI) / 180;
  var f =
    ['ABCDEFGH', 'JKLMNPQR', 'STUVWXYZ'][(gridZoneNum - 1) % 3]!.indexOf(
      gridSquare[0]!,
    ) + 1;
  var g = 'CDEFGHJKLMNPQRSTUVWXX'.indexOf(gridZoneAlpha);
  var h = ['ABCDEFGHJKLMNPQRSTUV', 'FGHJKLMNPQRSTUVABCDE'][
    (gridZoneNum - 1) % 2
  ]!.indexOf(gridSquare[1]!);
  var i = [
    1.1, 2.0, 2.8, 3.7, 4.6, 5.5, 6.4, 7.3, 8.2, 9.1, 0, 0.8, 1.7, 2.6, 3.5,
    4.4, 5.3, 6.2, 7.0, 7.9,
  ];
  var j = [0, 2, 2, 2, 4, 4, 6, 6, 8, 8, 0, 0, 0, 2, 2, 4, 4, 6, 6, 6];
  var k = i[g]!;
  var l = j[g]! + h / 10;
  if (l < k) l += 2;
  var m = f * 100000.0 + easting;
  var n = l * 1000000 + northing;
  m -= 500000.0;
  if (gridZoneAlpha < 'N') n -= 10000000.0;
  m /= 0.9996;
  n /= 0.9996;
  var o = n / 6367449.14570093;
  var p =
    o +
    0.0025188266133249035 * Math.sin(2.0 * o) +
    0.0000037009491206268 * Math.sin(4.0 * o) +
    0.0000000074477705265 * Math.sin(6.0 * o) +
    0.000000000017035994 * Math.sin(8.0 * o);
  var q = Math.tan(p);
  var r = q * q;
  var s = r * r;
  var t = Math.cos(p);
  var u = 0.006739496819936062 * Math.pow(t, 2);
  var v = 40680631590769 / (6356752.314 * Math.sqrt(1 + u));
  var w = v;
  var x = 1.0 / (w * t);
  w *= v;
  var y = q / (2.0 * w);
  w *= v;
  var z = 1.0 / (6.0 * w * t);
  w *= v;
  var aa = q / (24.0 * w);
  w *= v;
  var ab = 1.0 / (120.0 * w * t);
  w *= v;
  var ac = q / (720.0 * w);
  w *= v;
  var ad = 1.0 / (5040.0 * w * t);
  w *= v;
  var ae = q / (40320.0 * w);
  var af = -1.0 - u;
  var ag = -1.0 - 2 * r - u;
  var ah =
    5.0 + 3.0 * r + 6.0 * u - 6.0 * r * u - 3.0 * (u * u) - 9.0 * r * (u * u);
  var ai = 5.0 + 28.0 * r + 24.0 * s + 6.0 * u + 8.0 * r * u;
  var aj = -61.0 - 90.0 * r - 45.0 * s - 107.0 * u + 162.0 * r * u;
  var ak = -61.0 - 662.0 * r - 1320.0 * s - 720.0 * (s * r);
  var al = 1385.0 + 3633.0 * r + 4095.0 * s + 1575 * (s * r);
  var lat =
    p +
    y * af * (m * m) +
    aa * ah * Math.pow(m, 4) +
    ac * aj * Math.pow(m, 6) +
    ae * al * Math.pow(m, 8);
  var lng =
    e +
    x * m +
    z * ag * Math.pow(m, 3) +
    ab * ai * Math.pow(m, 5) +
    ad * ak * Math.pow(m, 7);
  lat = (lat * 180) / Math.PI;
  lng = (lng * 180) / Math.PI;
  return [lat, lng];
}
